import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Icon } from './Icon';
import './Stepper.scss';

/** A step of a `Stepper` (Angular `<mat-step>` with `<ng-template matStepLabel>`); it renders nothing on its own. */
export interface StepProps {
  /** The header text (Angular `label` / `matStepLabel`). */
  label?: ReactNode;
  /**
   * Angular `[completed]`. Left undefined a step is completed once the user has moved away from it (Angular's
   * `interacted`); set it to decide by yourself (what `[stepControl]` validity did).
   */
  completed?: boolean;
  /** Angular `[editable]` (default true): whether the user can come back to the step after moving on. */
  editable?: boolean;
  /** Angular `[optional]`: shows "Optional" under the label and is skipped by the linear check. */
  optional?: boolean;
  /** Angular `aria-label`. */
  ariaLabel?: string;
  /** Angular `aria-labelledby`. */
  ariaLabelledby?: string;
  children?: ReactNode;
}

export function Step(props: StepProps): null {
  void props;
  return null;
}

/** Angular `StepperSelectionEvent` (without the step references). */
export interface StepperSelectionEvent {
  selectedIndex: number;
  previouslySelectedIndex: number;
}

/** What `@ViewChild(MatStepper)` gave: `next()`, `previous()`, `reset()` and the current `selectedIndex`. */
export interface StepperHandle {
  /** Selects the next step (no-op on the last one); like Angular it marks the current step as interacted first. */
  next(): void;
  previous(): void;
  /** Back to the first step, with every step's interacted state cleared. */
  reset(): void;
  /** The index of the selected step (always current, even between renders). */
  readonly selectedIndex: number;
}

export interface StepperProps {
  /**
   * Angular `[linear]`: a step can only be reached once every step before it is completed (or optional). Note that
   * `next()` marks the current step as interacted first, so it always lets the user move forward one step.
   */
  linear?: boolean;
  /**
   * Angular `[selectedIndex]`: when it changes the stepper goes to that step (through the same checks and with a
   * `selectionChange`, like Angular's setter). Leave undefined to let the stepper manage it.
   */
  selectedIndex?: number;
  /** Angular `(selectionChange)`. */
  onSelectionChange?: (event: StepperSelectionEvent) => void;
  /** Angular `[orientation]` (default horizontal). */
  orientation?: 'horizontal' | 'vertical';
  /** Angular `[labelPosition]` for the horizontal stepper (default `end`). */
  labelPosition?: 'end' | 'bottom';
  /** Angular `[headerPosition]` for the horizontal stepper (default `top`). */
  headerPosition?: 'top' | 'bottom';
  /** Extra classes on the `mat-stepper` host. */
  className?: string;
  /** `<Step>` children. */
  children: ReactNode;
}

type IndicatorState = 'number' | 'edit' | 'done';

/**
 * `<mat-stepper>` (Angular Material 17, MDC): the same DOM and class names (`mat-stepper-horizontal`,
 * `mat-step-header`, `mat-step-icon-state-*`, `mat-horizontal-stepper-content-inactive`, ...) so the ported
 * `_stepper`/`_default` rules apply unchanged. Like Material, every step's content is in the DOM and the inactive
 * ones are collapsed. Header click, Enter/Space, Home/End and the arrow keys behave as the CDK stepper's do.
 */
export const Stepper = forwardRef<StepperHandle, StepperProps>(function Stepper(
  {
    linear = false,
    selectedIndex: selectedIndexProp,
    onSelectionChange,
    orientation = 'horizontal',
    labelPosition = 'end',
    headerPosition = 'top',
    className = '',
    children,
  },
  ref,
) {
  const groupId = useId();
  const steps = Children.toArray(children).filter(
    (child): child is ReactElement<StepProps> => isValidElement(child) && child.type === Step,
  );

  // The state drives rendering; the refs mirror it so handlers see the value at once, as Angular's fields did.
  const [selected, setSelected] = useState(selectedIndexProp ?? 0);
  const [interacted, setInteracted] = useState<boolean[]>([]);
  const [focusIndex, setFocusIndex] = useState(selectedIndexProp ?? 0);
  const selectedRef = useRef(selected);
  const interactedRef = useRef<boolean[]>([]);
  const stepsRef = useRef(steps);
  const linearRef = useRef(linear);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    stepsRef.current = steps;
    linearRef.current = linear;
    onSelectionChangeRef.current = onSelectionChange;
  });

  /** Angular `step.completed`: the override, else whether the user has moved away from the step. */
  const isCompleted = (step: ReactElement<StepProps>, index: number, list: readonly boolean[]) =>
    step.props.completed ?? list[index] ?? false;

  const markInteracted = (index: number) => {
    if (index < 0 || interactedRef.current[index]) return;
    const next = [...interactedRef.current];
    next[index] = true;
    interactedRef.current = next;
    setInteracted(next);
  };

  const anyControlsInvalidOrPending = (index: number) =>
    linearRef.current &&
    index >= 0 &&
    stepsRef.current
      .slice(0, index)
      .some((step, i) => !isCompleted(step, i, interactedRef.current) && !step.props.optional);

  const focusHeader = (index: number) => {
    setFocusIndex(index);
    headerRefs.current[index]?.focus();
  };

  const updateSelected = (newIndex: number) => {
    const previous = selectedRef.current;
    onSelectionChangeRef.current?.({ selectedIndex: newIndex, previouslySelectedIndex: previous });
    // If focus is inside the stepper it follows the selection, otherwise it could be lost when the content hides.
    if (rootRef.current?.contains(document.activeElement)) focusHeader(newIndex);
    else setFocusIndex(newIndex);
    selectedRef.current = newIndex;
    setSelected(newIndex);
  };

  /** Angular's `selectedIndex` setter. */
  const select = (index: number) => {
    const list = stepsRef.current;
    if (index < 0 || index >= list.length) {
      throw new Error('Stepper: Cannot assign out-of-bounds value to `selectedIndex`.');
    }
    markInteracted(selectedRef.current);
    if (
      selectedRef.current !== index &&
      !anyControlsInvalidOrPending(index) &&
      (index >= selectedRef.current || list[index].props.editable !== false)
    ) {
      updateSelected(index);
    }
  };

  useImperativeHandle(ref, () => ({
    next: () => select(Math.min(selectedRef.current + 1, stepsRef.current.length - 1)),
    previous: () => select(Math.max(selectedRef.current - 1, 0)),
    reset: () => {
      updateSelected(0);
      interactedRef.current = [];
      setInteracted([]);
    },
    get selectedIndex() {
      return selectedRef.current;
    },
  }));

  // Angular `[selectedIndex]` input: a later change goes through the setter (the first value is just the start).
  const lastSelectedProp = useRef(selectedIndexProp);
  useEffect(() => {
    if (selectedIndexProp === lastSelectedProp.current) return;
    lastSelectedProp.current = selectedIndexProp;
    if (selectedIndexProp !== undefined && selectedIndexProp !== selectedRef.current) select(selectedIndexProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndexProp]);

  // Steps can go away: keep the selection on a step that exists.
  const count = steps.length;
  const shown = count > 0 && selected >= count ? Math.max(count - 1, 0) : selected;

  const stepLabelId = (i: number) => `cdk-step-label-${groupId}-${i}`;
  const stepContentId = (i: number) => `cdk-step-content-${groupId}-${i}`;
  const isNavigable = (index: number) => isCompleted(steps[index], index, interacted) || shown === index || !linear;

  const indicatorState = (index: number): IndicatorState => {
    const current = shown === index;
    if (!isCompleted(steps[index], index, interacted) || current) return 'number';
    return steps[index].props.editable !== false ? 'edit' : 'done';
  };

  const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const hasModifier = event.altKey || event.shiftKey || event.ctrlKey || event.metaKey;
    const move = (to: number) => {
      event.preventDefault();
      focusHeader(to);
    };
    if (!hasModifier && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      select(focusIndex);
      return;
    }
    const last = count - 1;
    switch (event.key) {
      case 'Home':
        move(0);
        break;
      case 'End':
        move(last);
        break;
      case 'ArrowRight':
        move(focusIndex >= last ? 0 : focusIndex + 1);
        break;
      case 'ArrowLeft':
        move(focusIndex <= 0 ? last : focusIndex - 1);
        break;
      case 'ArrowDown':
        if (orientation === 'vertical') move(focusIndex >= last ? 0 : focusIndex + 1);
        break;
      case 'ArrowUp':
        if (orientation === 'vertical') move(focusIndex <= 0 ? last : focusIndex - 1);
        break;
    }
  };

  const horizontal = orientation === 'horizontal';

  const header = (step: ReactElement<StepProps>, i: number) => {
    const state = indicatorState(i);
    const { label, optional, ariaLabel, ariaLabelledby } = step.props;
    const hasLabel = label !== undefined && label !== null && label !== '' && label !== false;
    return (
      <div
        key={`header-${step.key ?? i}`}
        ref={(element) => {
          headerRefs.current[i] = element;
        }}
        className={`mat-step-header mat-primary ${horizontal ? 'mat-horizontal-stepper-header' : 'mat-vertical-stepper-header'}`}
        role="tab"
        tabIndex={focusIndex === i ? 0 : -1}
        id={stepLabelId(i)}
        aria-posinset={i + 1}
        aria-setsize={count}
        aria-controls={stepContentId(i)}
        aria-selected={shown === i}
        aria-label={ariaLabel || undefined}
        aria-labelledby={!ariaLabel && ariaLabelledby ? ariaLabelledby : undefined}
        aria-disabled={isNavigable(i) ? undefined : true}
        onClick={() => select(i)}
        onKeyDown={onHeaderKeyDown}
      >
        <div className="mat-step-header-ripple mat-focus-indicator"></div>
        <div className={`mat-step-icon-state-${state} mat-step-icon${shown === i ? ' mat-step-icon-selected' : ''}`}>
          <div className="mat-step-icon-content">
            {state === 'number' ? (
              <span aria-hidden="true">{i + 1}</span>
            ) : (
              <>
                <span className="cdk-visually-hidden">{state === 'done' ? 'Completed' : 'Editable'}</span>
                <Icon name={state === 'edit' ? 'create' : 'done'} aria-hidden="true" />
              </>
            )}
          </div>
        </div>
        <div
          className={`mat-step-label${isNavigable(i) ? ' mat-step-label-active' : ''}${shown === i ? ' mat-step-label-selected' : ''}`}
        >
          {hasLabel && <div className="mat-step-text-label">{label}</div>}
          {optional && <div className="mat-step-optional">Optional</div>}
        </div>
      </div>
    );
  };

  const hostClass = [
    horizontal ? 'mat-stepper-horizontal' : 'mat-stepper-vertical',
    horizontal && labelPosition === 'end' && 'mat-stepper-label-position-end',
    horizontal && labelPosition === 'bottom' && 'mat-stepper-label-position-bottom',
    headerPosition === 'bottom' && 'mat-stepper-header-position-bottom',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={rootRef} className={hostClass} role="tablist" aria-orientation={orientation}>
      {horizontal ? (
        <div className="mat-horizontal-stepper-wrapper">
          <div className="mat-horizontal-stepper-header-container">
            {steps.flatMap((step, i) => [
              header(step, i),
              ...(i < count - 1
                ? [<div key={`line-${step.key ?? i}`} className="mat-stepper-horizontal-line"></div>]
                : []),
            ])}
          </div>
          <div className="mat-horizontal-content-container">
            {steps.map((step, i) => (
              <div
                key={`content-${step.key ?? i}`}
                className={`mat-horizontal-stepper-content${shown !== i ? ' mat-horizontal-stepper-content-inactive' : ''}`}
                role="tabpanel"
                id={stepContentId(i)}
                aria-labelledby={stepLabelId(i)}
                style={shown !== i ? { visibility: 'hidden' } : undefined}
              >
                {step.props.children}
              </div>
            ))}
          </div>
        </div>
      ) : (
        steps.map((step, i) => (
          <div key={`step-${step.key ?? i}`} className="mat-step">
            {header(step, i)}
            <div className={`mat-vertical-content-container${i < count - 1 ? ' mat-stepper-vertical-line' : ''}`}>
              <div
                className={`mat-vertical-stepper-content${shown !== i ? ' mat-vertical-stepper-content-inactive' : ''}`}
                role="tabpanel"
                id={stepContentId(i)}
                aria-labelledby={stepLabelId(i)}
                style={shown !== i ? { visibility: 'hidden' } : undefined}
              >
                <div className="mat-vertical-content">{step.props.children}</div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
});
